import { useState, useEffect } from 'react';
import { RefreshCw, Image, CheckCircle, XCircle, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { populateAllMissingCachedCovers, getCoverCacheStats } from '@/utils/populateCachedCovers';
import { clearAllCachedCovers } from '@/utils/clearCachedCover';
import { supabase } from '@/integrations/supabase/client';

interface AIRecStats {
  totalAIBooks: number;
  aiBooksMissingCache: number;
  percentCached: number;
}

export function CoverCacheManager() {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [result, setResult] = useState<{ processed: number; succeeded: number; failed: number } | null>(null);
  const [stats, setStats] = useState<{ totalWithCovers: number; totalCached: number; totalMissing: number; percentCached: number } | null>(null);
  const [aiRecStats, setAiRecStats] = useState<AIRecStats | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const cacheStats = await getCoverCacheStats();
      setStats(cacheStats);

      // Get AI-recommended books stats
      const { count: totalAI } = await supabase
        .from('books')
        .select('*', { count: 'exact', head: true })
        .not('cover_url', 'is', null)
        .contains('data_sources', { ai_recommendation: true });

      const { count: aiMissing } = await supabase
        .from('books')
        .select('*', { count: 'exact', head: true })
        .eq('cover_cached', false)
        .not('cover_url', 'is', null)
        .contains('data_sources', { ai_recommendation: true });

      setAiRecStats({
        totalAIBooks: totalAI || 0,
        aiBooksMissingCache: aiMissing || 0,
        percentCached: totalAI ? Math.round(((totalAI - (aiMissing || 0)) / totalAI) * 100) : 100
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load cover cache statistics',
        variant: 'destructive'
      });
    }
  };

  const startPopulating = async () => {
    try {
      setProcessing(true);
      setProgress({ current: 0, total: 0 });
      setResult(null);

      toast({
        title: 'Starting Cover Cache',
        description: 'Processing books in background...'
      });

      const populateResult = await populateAllMissingCachedCovers(50, (current, total, batchResult) => {
        setProgress({ current, total });
        if (batchResult) {
          setResult(batchResult);
        }
      });

      setResult(populateResult);
      await loadStats();

      toast({
        title: 'Cover Caching Complete',
        description: `Processed ${populateResult.processed} books. ${populateResult.succeeded} succeeded, ${populateResult.failed} failed.`,
        variant: populateResult.failed === 0 ? 'default' : 'destructive'
      });
    } catch (error) {
      console.error('Error populating covers:', error);
      toast({
        title: 'Error',
        description: 'Failed to populate cached covers',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const triggerBackgroundJob = async () => {
    try {
      toast({
        title: 'Triggering Background Job',
        description: 'Starting background cover caching...'
      });

      const { data, error } = await supabase.functions.invoke('cache-covers-background');

      if (error) {
        throw error;
      }

      toast({
        title: 'Background Job Complete',
        description: `Processed ${data.processed} books. ${data.succeeded} succeeded, ${data.failed} failed.`
      });

      await loadStats();
    } catch (error) {
      console.error('Error triggering background job:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger background job',
        variant: 'destructive'
      });
    }
  };

  const cacheAIRecommendations = async () => {
    try {
      setProcessing(true);
      
      toast({
        title: 'Caching AI Recommendations',
        description: 'Prioritizing AI-recommended books...'
      });

      // Get AI-recommended books without cached covers
      const { data: aiBooks, error } = await supabase
        .from('books')
        .select('id')
        .eq('cover_cached', false)
        .not('cover_url', 'is', null)
        .contains('data_sources', { ai_recommendation: true })
        .limit(100);

      if (error) throw error;

      if (!aiBooks || aiBooks.length === 0) {
        toast({
          title: 'No Books to Cache',
          description: 'All AI-recommended books already have cached covers!'
        });
        return;
      }

      const bookIds = aiBooks.map(b => b.id);

      // Use enrich-books edge function to cache covers
      const { data, error: cacheError } = await supabase.functions.invoke('enrich-books', {
        body: { 
          action: 'cache-covers',
          bookIds,
          batchSize: bookIds.length
        }
      });

      if (cacheError) throw cacheError;

      toast({
        title: 'Success',
        description: `Cached ${data.cached} AI-recommended book covers!`
      });

      await loadStats();
    } catch (error) {
      console.error('Error caching AI recommendations:', error);
      toast({
        title: 'Error',
        description: 'Failed to cache AI recommendation covers',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  const clearAllCaches = async () => {
    if (!confirm('Are you sure you want to clear ALL cached covers? This will force all covers to reload from their original sources.')) {
      return;
    }

    try {
      setProcessing(true);
      
      toast({
        title: 'Clearing All Cached Covers',
        description: 'This may take a moment...'
      });

      const result = await clearAllCachedCovers();

      if (result.success) {
        toast({
          title: 'Success',
          description: `Cleared ${result.count} cached covers!`
        });
        await loadStats();
      } else {
        throw new Error(result.error || 'Failed to clear caches');
      }
    } catch (error) {
      console.error('Error clearing caches:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to clear cached covers',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="w-5 h-5" />
          Cover Cache Manager
        </CardTitle>
        <CardDescription>
          Populate cached covers for faster loading. Cached covers load instantly from storage.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Stats */}
        {stats && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Cache Progress</span>
              <Badge variant={stats.percentCached > 80 ? 'default' : 'secondary'}>
                {stats.percentCached}%
              </Badge>
            </div>
            <Progress value={stats.percentCached} className="h-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{stats.totalCached.toLocaleString()} cached</span>
              <span>{stats.totalMissing.toLocaleString()} remaining</span>
            </div>
          </div>
        )}

        {/* AI Recommendations Stats */}
        {aiRecStats && aiRecStats.totalAIBooks > 0 && (
          <div className="space-y-2 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">AI Recommendations</span>
              </div>
              <Badge variant={aiRecStats.percentCached > 80 ? 'default' : 'destructive'}>
                {aiRecStats.percentCached}%
              </Badge>
            </div>
            <Progress value={aiRecStats.percentCached} className="h-2" />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{(aiRecStats.totalAIBooks - aiRecStats.aiBooksMissingCache).toLocaleString()} cached</span>
              <span className="text-destructive font-medium">{aiRecStats.aiBooksMissingCache.toLocaleString()} need caching</span>
            </div>
          </div>
        )}

        {/* Progress */}
        {processing && progress.total > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">Processing...</span>
              <span className="text-sm text-muted-foreground">
                {progress.current} / {progress.total}
              </span>
            </div>
            <Progress value={(progress.current / progress.total) * 100} className="h-2" />
          </div>
        )}

        {/* Result */}
        {result && !processing && (
          <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm">{result.succeeded} succeeded</span>
            </div>
            {result.failed > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-destructive" />
                <span className="text-sm">{result.failed} failed</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {/* Priority: AI Recommendations */}
          {aiRecStats && aiRecStats.aiBooksMissingCache > 0 && (
            <Button
              onClick={cacheAIRecommendations}
              disabled={processing}
              variant="default"
              className="w-full"
            >
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              <Sparkles className="w-4 h-4 mr-2" />
              Cache AI Recommendations ({aiRecStats.aiBooksMissingCache})
            </Button>
          )}
          
          <div className="flex gap-2">
            <Button
              onClick={loadStats}
              variant="outline"
              disabled={processing}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={startPopulating}
              disabled={processing}
              variant="secondary"
            >
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              All Books (1000)
            </Button>
            <Button
              onClick={triggerBackgroundJob}
              variant="secondary"
              disabled={processing}
            >
              Background (100)
            </Button>
          </div>

          <Button
            onClick={clearAllCaches}
            variant="destructive"
            disabled={processing}
            className="w-full"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Cached Covers
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          💡 Covers are now <strong>automatically cached</strong> during enrichment. Use "Cache AI Recommendations" to fix existing AI-recommended books with missing covers.
        </p>
      </CardContent>
    </Card>
  );
}
